import { useRef } from 'react';
import { Trash2, Plus } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import { labelStyle, inputStyle } from './adminFieldStyles';

const BUCKET = 'products_images';
const MAX_IMAGES = 4;

// Entries created before multi-image support only have a single `image`
// string; new/edited ones carry an `images` array. Always edit through the
// array, keeping at least one (possibly empty) slot so the UI has something
// to render.
const entryImages = (entry) => {
  if (Array.isArray(entry.images) && entry.images.length > 0) return entry.images;
  if (entry.image) return [entry.image];
  return [''];
};

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Free-text fields translated per-entry, mirroring the `${field}_en/_zh/_ja`
// convention every other bilingual DB column in this project uses (read back
// via tField() on the public artist page — see api/_customShowHistory.js).
const TRANSLATABLE_FIELDS = [
  { key: 'eventName', label: 'Custom o evento', maxLength: 200, multiline: false },
  { key: 'customName', label: 'Nombre del custom', maxLength: 200, multiline: false },
  { key: 'platform', label: 'Plataforma (figura base)', maxLength: 200, multiline: false },
  { key: 'platformCreator', label: 'Creador de la plataforma', maxLength: 200, multiline: false },
  { key: 'materials', label: 'Materiales', maxLength: 300, multiline: false },
  { key: 'dimensions', label: 'Dimensiones', maxLength: 200, multiline: false },
  { key: 'story', label: 'Historia', maxLength: 1000, multiline: true },
];

const TRANSLATION_LANGS = [
  { suffix: 'en', label: 'English' },
  { suffix: 'zh', label: '中文' },
  { suffix: 'ja', label: '日本語' },
];

let localIdCounter = 0;
// crypto.randomUUID() isn't available in every environment this admin form
// might run in (older Safari, non-HTTPS dev tunnels) — fall back to a
// per-session counter. Either way this id is only a React key / edit
// target, never referenced by another table, so it's fine to reassign it
// server-side if it's ever missing or duplicated.
function newLocalId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  localIdCounter += 1;
  return `local-${Date.now()}-${localIdCounter}`;
}

/**
 * Repeatable custom-show-history editor: a timeline of customs/events the
 * artist has participated in (toy customization shows, conventions, private
 * commissions). Rendered as a card per entry on the public artist page,
 * below the bio/creator info section — entirely optional, hidden when empty.
 * Used by AdminArtistForm.
 *
 * Props:
 *   value {Array}       — [{id,eventName,platform,platformCreator,image,images,customName,story,year,month,price,soldOut,materials,dimensions}]
 *                         `platformCreator` is the optional studio/manufacturer
 *                         of the base platform (e.g. "Medicom Toy"), distinct
 *                         from the artist customizing it. `images` is an array
 *                         of up to 4 photo URLs; `image` is kept in sync with
 *                         images[0] for legacy readers, plus optional
 *                         `${field}_en/_zh/_ja` translations for eventName/
 *                         platform/platformCreator/customName/materials/
 *                         dimensions/story, auto-filled at save time if left
 *                         blank (see api/_customShowHistory.js)
 *   onChange(next)      — called with the updated array
 *   onUploadingChange(bool) — bubbled up so the parent form can disable submit
 */
const CustomShowHistoryField = ({ value = [], onChange, onUploadingChange }) => {
  const uploadingIds = useRef(new Set());

  const reportUploading = (key, isUploading) => {
    if (isUploading) uploadingIds.current.add(key); else uploadingIds.current.delete(key);
    onUploadingChange?.(uploadingIds.current.size > 0);
  };

  const update = (idx, patch) => {
    const next = value.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    onChange(next);
  };

  // Keep the legacy `image` key in sync with images[0] so anything reading
  // the old shape before the next save stays consistent (the API does the
  // same on write — see api/_customShowHistory.js).
  const commitImages = (idx, imgs) => {
    const next = imgs.length > 0 ? imgs : [''];
    update(idx, { images: next, image: next[0] || '' });
  };

  const setImage = (idx, imgIdx, url) => {
    const imgs = [...entryImages(value[idx])];
    imgs[imgIdx] = url;
    commitImages(idx, imgs);
  };

  const addImage = (idx) => {
    const imgs = [...entryImages(value[idx]), ''];
    commitImages(idx, imgs.slice(0, MAX_IMAGES));
  };

  const removeImage = (idx, imgIdx) => {
    commitImages(idx, entryImages(value[idx]).filter((_, i) => i !== imgIdx));
  };

  const addEntry = () => {
    onChange([
      ...value,
      {
        id: newLocalId(),
        eventName: '',
        platform: '',
        platformCreator: '',
        image: '',
        images: [''],
        customName: '',
        story: '',
        year: '',
        month: '',
        price: '',
        soldOut: false,
        materials: '',
        dimensions: '',
      },
    ]);
  };

  const removeEntry = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={labelStyle}>Historial de customs (opcional)</label>
      <p style={{ fontSize: '0.82rem', color: '#777', margin: '0 0 0.75rem' }}>
        Línea de tiempo de customs o eventos en los que el artista ha participado.
        Se muestra como sección en el perfil público solo cuando hay al menos una entrada.
      </p>

      {value.map((entry, idx) => (
        <div
          key={entry.id || idx}
          style={{
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '0.85rem',
            background: '#fafbfc',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '-0.5rem' }}>
            <button
              type="button"
              onClick={() => removeEntry(idx)}
              title="Eliminar entrada"
              style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '0.3rem' }}
            >
              <Trash2 size={18} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Año</label>
              <input
                type="number"
                value={entry.year ?? ''}
                onChange={(e) => update(idx, { year: e.target.value })}
                placeholder="2024"
                min="1900"
                max={new Date().getFullYear() + 1}
                required
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Mes (opcional)</label>
              <select
                value={entry.month ?? ''}
                onChange={(e) => update(idx, { month: e.target.value })}
                style={inputStyle}
              >
                <option value="">Sin especificar</option>
                {MONTHS.map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Nombre del custom (opcional)</label>
              <input
                type="text"
                value={entry.customName || ''}
                onChange={(e) => update(idx, { customName: e.target.value })}
                placeholder="Ej. Nocturno"
                maxLength={200}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Custom o evento (opcional)</label>
              <input
                type="text"
                value={entry.eventName}
                onChange={(e) => update(idx, { eventName: e.target.value })}
                placeholder="Ej. Toy Con MX 2024"
                maxLength={200}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Plataforma (figura base)</label>
              <input
                type="text"
                value={entry.platform}
                onChange={(e) => update(idx, { platform: e.target.value })}
                placeholder="Ej. Bearbrick 400%"
                maxLength={200}
                required
                style={inputStyle}
              />
              <div style={{ marginTop: '0.6rem' }}>
                <label style={labelStyle}>Creador de la plataforma (opcional)</label>
                <input
                  type="text"
                  value={entry.platformCreator || ''}
                  onChange={(e) => update(idx, { platformCreator: e.target.value })}
                  placeholder="Ej. Medicom Toy"
                  maxLength={200}
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Precio en MXN (opcional)</label>
              <input
                type="number"
                value={entry.price ?? ''}
                onChange={(e) => update(idx, { price: e.target.value })}
                placeholder="3500"
                min="0"
                step="0.01"
                disabled={!!entry.soldOut}
                style={inputStyle}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.4rem', fontSize: '0.82rem', color: '#555', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!entry.soldOut}
                  onChange={(e) => update(idx, { soldOut: e.target.checked })}
                />
                Vendido / agotado (oculta el precio en el perfil público)
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Historia (opcional)</label>
            <textarea
              value={entry.story || ''}
              onChange={(e) => update(idx, { story: e.target.value })}
              placeholder="Breve historia detrás de esta pieza o evento."
              maxLength={1000}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Materiales</label>
            <input
              type="text"
              value={entry.materials}
              onChange={(e) => update(idx, { materials: e.target.value })}
              placeholder="Ej. Resina, acrílico"
              maxLength={300}
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Dimensiones (opcional)</label>
            <input
              type="text"
              value={entry.dimensions || ''}
              onChange={(e) => update(idx, { dimensions: e.target.value })}
              placeholder="Ej. 28 x 15 x 12 cm"
              maxLength={200}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.1rem' }}>
            <label style={labelStyle}>Imágenes del custom (hasta {MAX_IMAGES})</label>
            <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 0.5rem' }}>
              La primera es la principal. En el perfil público se muestran en un carrusel en el espacio de la imagen.
            </p>
            {entryImages(entry).map((img, imgIdx) => (
              <div key={imgIdx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ImageUploadField
                    label={imgIdx === 0 ? 'Imagen 1 (principal)' : `Imagen ${imgIdx + 1}`}
                    value={img || ''}
                    onChange={(url) => setImage(idx, imgIdx, url)}
                    bucket={BUCKET}
                    onUploadingChange={(isUploading) => reportUploading(`${entry.id || idx}:img${imgIdx}`, isUploading)}
                  />
                </div>
                {entryImages(entry).length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeImage(idx, imgIdx)}
                    title="Quitar imagen"
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '0.3rem', marginTop: '1.5rem' }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
            {entryImages(entry).length < MAX_IMAGES && (
              <button
                type="button"
                onClick={() => addImage(idx)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.35rem 0.8rem',
                  background: 'white',
                  border: '1px dashed #2ca9e1',
                  borderRadius: '8px',
                  color: '#2ca9e1',
                  fontSize: '0.82rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                <Plus size={14} /> Agregar imagen
              </button>
            )}
          </div>

          <details style={{ margin: '0.85rem 0 0', padding: '0.65rem 0.85rem', background: '#f4f6f8', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', color: '#333' }}>
              Traducciones (English / 中文 / 日本語) — opcional
            </summary>
            <p style={{ fontSize: '0.76rem', color: '#777', margin: '0.5rem 0 0.75rem' }}>
              Si dejas alguna vacía, se traduce automáticamente al guardar (Claude Haiku).
            </p>
            {TRANSLATION_LANGS.map(({ suffix, label: langLabel }, langIdx) => (
              <div key={suffix}>
                {langIdx > 0 && <hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: '0.85rem 0' }} />}
                <h5 style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {langLabel}
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                  {TRANSLATABLE_FIELDS.filter((f) => !f.multiline).map((f) => (
                    <div key={f.key} style={{ marginBottom: '0.6rem' }}>
                      <label style={labelStyle}>{f.label} ({langLabel})</label>
                      <input
                        type="text"
                        value={entry[`${f.key}_${suffix}`] || ''}
                        onChange={(e) => update(idx, { [`${f.key}_${suffix}`]: e.target.value })}
                        maxLength={f.maxLength}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>
                {TRANSLATABLE_FIELDS.filter((f) => f.multiline).map((f) => (
                  <div key={f.key} style={{ marginBottom: '0.6rem' }}>
                    <label style={labelStyle}>{f.label} ({langLabel})</label>
                    <textarea
                      value={entry[`${f.key}_${suffix}`] || ''}
                      onChange={(e) => update(idx, { [`${f.key}_${suffix}`]: e.target.value })}
                      maxLength={f.maxLength}
                      rows={2}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </details>
        </div>
      ))}

      <button
        type="button"
        onClick={addEntry}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.5rem 1rem',
          background: 'white',
          border: '1px dashed #2ca9e1',
          borderRadius: '8px',
          color: '#2ca9e1',
          fontSize: '0.88rem',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        <Plus size={16} /> Agregar custom
      </button>
    </div>
  );
};

export default CustomShowHistoryField;
